import React from 'react';
import { Box, Card, CircularProgress, Typography } from '@mui/material';
import { Network } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ThirdPartyUsageRow } from '../../types';
import { formatTokens } from './dashboard-format';

interface ThirdPartyTotal {
  messages: number;
  tokens: number;
  credits: number;
  toolCalls: number;
}

interface ThirdPartyUsageCardProps {
  tenantRows: ThirdPartyUsageRow[];
  agentRows: ThirdPartyUsageRow[];
  total: ThirdPartyTotal;
  loading: boolean;
  error: string | null;
}

export const ThirdPartyUsageCard: React.FC<ThirdPartyUsageCardProps> = ({
  tenantRows,
  agentRows,
  total,
  loading,
  error,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      className="mb-6 border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:mb-8"
      elevation={0}
    >
      <Box className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Box className="flex items-center gap-2">
            <Network size={18} className="text-emerald-600 dark:text-emerald-400" />
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-white">
              {t('dashboard.thirdPartyUsage.title')}
            </Typography>
          </Box>
          <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
            {t('dashboard.thirdPartyUsage.subtitle')}
          </Typography>
        </Box>
        <Box className="grid grid-cols-2 gap-3 text-right md:grid-cols-4">
          <SummaryValue
            label={t('dashboard.thirdPartyUsage.summary.messages')}
            value={total.messages}
          />
          <SummaryValue
            label={t('dashboard.thirdPartyUsage.summary.tokens')}
            value={formatTokens(total.tokens)}
          />
          <SummaryValue
            label={t('dashboard.thirdPartyUsage.summary.credits')}
            value={total.credits}
          />
          <SummaryValue
            label={t('dashboard.thirdPartyUsage.summary.tools')}
            value={total.toolCalls}
          />
        </Box>
      </Box>

      {loading && (
        <Box className="flex min-h-44 items-center justify-center rounded-lg border border-dashed border-gray-200 dark:border-slate-700">
          <CircularProgress size={24} />
        </Box>
      )}
      {!loading && error && (
        <Box className="rounded-lg border border-dashed border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
          <Typography variant="body2" className="text-amber-700 dark:text-amber-300">
            {error}
          </Typography>
        </Box>
      )}
      {!loading && !error && (
        <Box className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ThirdPartyUsageList
            title={t('dashboard.thirdPartyUsage.byTenant')}
            emptyText={t('dashboard.thirdPartyUsage.empty')}
            rows={tenantRows}
            getLabel={(row) =>
              row.external_tenant_id ||
              row.client_id ||
              t('dashboard.thirdPartyUsage.unknownTenant')
            }
          />
          <ThirdPartyUsageList
            title={t('dashboard.thirdPartyUsage.byAgent')}
            emptyText={t('dashboard.thirdPartyUsage.empty')}
            rows={agentRows}
            getLabel={(row) => row.agent_public_id || t('dashboard.thirdPartyUsage.unknownAgent')}
          />
        </Box>
      )}
    </Card>
  );
};

const SummaryValue: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
      {label}
    </Typography>
    <Typography variant="subtitle2" className="font-semibold text-gray-900 dark:text-white">
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Typography>
  </Box>
);

const ThirdPartyUsageList: React.FC<{
  title: string;
  emptyText: string;
  rows: ThirdPartyUsageRow[];
  getLabel: (row: ThirdPartyUsageRow) => string;
}> = ({ title, emptyText, rows, getLabel }) => (
  <Box className="rounded-lg border border-gray-100 p-4 dark:border-slate-700">
    <Typography variant="subtitle2" className="mb-3 font-semibold text-gray-900 dark:text-white">
      {title}
    </Typography>
    <Box className="space-y-3">
      {rows.map((row, index) => (
        <Box
          key={`${getLabel(row)}-${index}`}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3"
        >
          <Box className="min-w-0">
            <Typography
              variant="body2"
              className="truncate font-medium text-gray-900 dark:text-white"
            >
              {getLabel(row)}
            </Typography>
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              {row.messages.toLocaleString()} messages · {row.tool_calls.toLocaleString()} tools
            </Typography>
          </Box>
          <Box className="text-right">
            <Typography variant="body2" className="font-semibold text-gray-900 dark:text-white">
              {formatTokens(row.total_tokens)}
            </Typography>
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              {row.cost_credits.toLocaleString()} credits
            </Typography>
          </Box>
        </Box>
      ))}
      {rows.length === 0 && (
        <Typography variant="body2" className="py-4 text-center text-gray-500 dark:text-slate-400">
          {emptyText}
        </Typography>
      )}
    </Box>
  </Box>
);
